"use client";

import { usePathname, useRouter } from "next/navigation";
import { ReactNode, createContext, useContext, useMemo, useState } from "react";

import { getFormElements } from "@/services/api/server/forms";
import { User } from "@/services/api/server/users";

import { Element } from "@/typedef/forms";
import { Trial } from "@/typedef/trial";

import { stepsHelper } from "./helper";

type Props = {
  children: ReactNode;
  questions?: Element[];
  step: number | null;
  trial: Trial;
  trialPractice: { id: number; name: string } | null;
  trialForm: { id: number; formKey: string } | null;
  user: User | null;
};

export type Form = {
  answers?: Record<string, unknown>;
  contact?: {
    method?: "EMAIL" | "PHONE" | null;
    phone?: string | null;
    email?: string | null;
  };
  pet?:
    | {
        id: string;
        practices?: { id: number; name: string }[];
      }
    | FormPet;
  trialForm: { id: number; formKey: string } | null;
  trialPractice: { id: number; name: string } | null;
  user?: { email?: string; firstName?: string; lastName?: string };
};

export type FormPet = {
  name?: string;
  gender?: "M" | "F";
  breeds?: number[];
  species?: number;
  sterilized?: boolean;
  celebrationDay?: string;
  celebrationType?: "ADOPTION_DAY" | "BIRTHDAY";
  practices?: { id: number; name: string }[];
};

type State = {
  data: Form;
  questions?: Element[];
};

const WizardContext = createContext({
  state: {
    currentStep: {
      id: 0,
      title: "",
      description: "",
    },
    data: {
      trialForm: null,
      trialPractice: null,
      user: {},
    } as Form,
    isFirst: true,
    isLast: false,
    steps: [
      {
        id: 1,
        title: "",
      },
    ],
  },
  questions: [] as Element[],
  trial: {} as Trial,
  actions: {
    next: () => {},
    prev: () => {},
    go: (_step: number) => {},
    complete: () => {},
    goBackCheck: (_step: number) => {},
    setData: (_data: Partial<Form>) => {},
    getQuestions: (_data: Form["answers"]) => Promise.resolve(),
  },
});

export const useWizard = () => useContext(WizardContext);

export default function WizardProvider({
  children,
  questions = [],
  step,
  trial,
  trialForm,
  trialPractice,
  user,
}: Props) {
  const pathname = usePathname();
  const router = useRouter();

  const [state, setState] = useState<State>({
    data: {
      contact: {
        method: user?.contactType,
        phone: user?.phone,
        email: user?.email,
      },
      trialForm,
      trialPractice,
    },
    questions,
  });

  const { currentStep, nextStepIndex, prevStepIndex, isFirst, isLast, steps } = useMemo(
    () => stepsHelper({ step, trial, trialPractice, userId: user?.id }),
    [step, trial, trialPractice, user?.id],
  );

  const handleData = <T extends object>(data?: T) => {
    setState((old) => ({
      ...old,
      data: {
        ...old.data,
        ...data,
      },
    }));
  };

  const handleGetQuestions = async (data: Form["answers"] = {}) => {
    if (trialForm) {
      const { data: questions } = await getFormElements({ key: trialForm.formKey, data });

      setState((old) => ({
        ...old,
        questions: questions?.elements ?? [],
      }));
    }
  };

  const goBackCheck = (step: number) => {
    const stepZero = !!user?.id || !!state.data.user;
    const stepOne = !trial.practices.length || !!state.data.trialPractice;
    const stepTwo = !!state.data.pet;
    const stepThree = !trialForm || !!state.data.answers;

    const valid = (() => {
      if (step === 1) {
        return stepZero;
      }

      if (step === 2) {
        return stepOne;
      }

      if (step === 3) {
        return stepOne && stepTwo;
      }

      if (step === 4 || step == 5) {
        return stepOne && stepTwo && stepThree;
      }
    })();

    if (!valid) {
      router.replace(`${pathname}?step=${prevStepIndex}`);
    }
  };

  const value = {
    state: {
      currentStep,
      data: state.data,
      isFirst,
      isLast,
      steps,
    },
    questions: state.questions ?? [],
    trial,
    actions: {
      next() {
        router.replace(`${pathname}?step=${nextStepIndex}`);
      },
      prev() {
        router.replace(`${pathname}?step=${prevStepIndex}`);
      },
      go(step: number) {
        router.replace(`${pathname}?step=${step}`);
      },
      complete() {
        router.replace(`${pathname}/complete`);
      },
      goBackCheck,
      setData: handleData,
      getQuestions: handleGetQuestions,
    },
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}
